import { useEffect, useState } from 'react';
import { keyframes } from '@emotion/react';
import { Box, CircularProgress, Skeleton, Stack, Typography, alpha, useTheme } from '@mui/material';
import { CheckCircle, ErrorOutline, Flight } from '@mui/icons-material';
import { getPartnerLogo } from '../../service/logo.service';

// Tela de "pesquisando" enquanto as seguradoras respondem (leva ~10 segundos).
// Mostra cada seguradora com o seu status, uma barra com um aviãozinho e
// frases que mudam, para a espera parecer mais curta.

const COMPANY_ORDER = ['assist-card', 'universal-assistance', 'affinity'];

const phrasesFor = (destination) => [
  'Estamos pesquisando as melhores coberturas para você...',
  'Consultando Assist Card, Universal Assistance e Affinity...',
  'Comparando preços e coberturas médicas...',
  destination === 'Europa'
    ? 'Conferindo a exigência Schengen de €30 mil...'
    : `Conferindo as coberturas para ${destination || 'o seu destino'}...`,
  'Quase lá! Organizando os melhores planos...',
];

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pop = keyframes`
  0% { transform: scale(0.6); opacity: 0; }
  70% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0%, 100% { transform: translate(-50%, -50%) rotate(90deg) translateX(0); }
  50% { transform: translate(-50%, -50%) rotate(90deg) translateX(-3px); }
`;

// Barra "cresce" com o tempo (até 90%) e com cada seguradora que responde.
const useProgress = (doneRatio) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const timer = setInterval(() => setElapsed((Date.now() - start) / 1000), 200);
    return () => clearInterval(timer);
  }, []);
  const byTime = 90 * (1 - Math.exp(-elapsed / 6));
  return Math.min(100, Math.max(byTime, doneRatio * 100));
};

const CompanyStatus = ({ company, status }) => {
  const theme = useTheme();
  const done = status?.status === 'done';
  const failed = status?.status === 'failed';

  return (
    <Stack
      alignItems="center"
      spacing={1}
      sx={{
        p: { xs: 1.25, md: 2 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: done ? 'success.light' : 'grey.100',
        backgroundColor: done ? alpha(theme.palette.success.main, 0.06) : 'background.paper',
        transition: 'all 0.4s ease',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          height: { xs: 22, md: 32 },
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          opacity: done || failed ? 1 : 0.55,
          transition: 'opacity 0.4s',
          '& svg': { height: '100%', width: 'auto', maxWidth: '100%' },
        }}
      >
        {getPartnerLogo(company)?.component.dark}
      </Box>
      <Box sx={{ height: 22, display: 'flex', alignItems: 'center' }}>
        {done && (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ color: 'success.dark', animation: `${pop} 0.4s ease-out` }}
          >
            <CheckCircle sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, fontWeight: 700 }}>
              {status.count} {status.count === 1 ? 'plano' : 'planos'}
            </Typography>
          </Stack>
        )}
        {failed && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.disabled' }}>
            <ErrorOutline sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' } }}>Indisponível</Typography>
          </Stack>
        )}
        {!done && !failed && (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
            <CircularProgress size={14} thickness={5} />
            <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' } }}>Consultando</Typography>
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

const SearchProgress = ({ companyStatus = {}, destination, showSkeletons = true }) => {
  const theme = useTheme();
  const phrases = phrasesFor(destination);
  const [phraseIndex, setPhraseIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(
      () => setPhraseIndex((i) => Math.min(i + 1, phrases.length - 1)),
      2500
    );
    return () => clearInterval(timer);
  }, [phrases.length]);

  const finished = COMPANY_ORDER.filter((c) =>
    ['done', 'failed'].includes(companyStatus[c]?.status)
  ).length;
  const progress = useProgress(finished / COMPANY_ORDER.length);

  return (
    <Box sx={{ mt: 3 }} role="status" aria-live="polite">
      <Box
        sx={{
          p: { xs: 2, md: 3.5 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(
            theme.palette.primary.main,
            0.02
          )})`,
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.15),
        }}
      >
        <Typography
          key={phraseIndex}
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.05rem', md: '1.3rem' },
            textAlign: 'center',
            minHeight: { xs: '2.6em', md: '1.6em' },
            animation: `${fadeIn} 0.5s ease-out`,
          }}
        >
          {phrases[phraseIndex]}
        </Typography>

        {/* Barra com o aviãozinho */}
        <Box sx={{ position: 'relative', mx: { xs: 1, md: 4 }, mt: 2.5, mb: 3, height: 8 }}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: 4,
              backgroundImage: `repeating-linear-gradient(90deg, ${theme.palette.grey[100]} 0 10px, transparent 10px 16px)`,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${progress}%`,
              borderRadius: 4,
              backgroundColor: 'primary.main',
              transition: 'width 0.4s ease-out',
            }}
          />
          <Flight
            sx={{
              position: 'absolute',
              top: '50%',
              left: `${progress}%`,
              color: 'primary.main',
              fontSize: { xs: 26, md: 30 },
              transition: 'left 0.4s ease-out',
              animation: `${float} 1.2s ease-in-out infinite`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: { xs: 1, md: 2 } }}>
          {COMPANY_ORDER.map((company) => (
            <CompanyStatus key={company} company={company} status={companyStatus[company]} />
          ))}
        </Box>
      </Box>

      {/* Cards "fantasma" no lugar dos destaques */}
      {showSkeletons && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2.5,
            mt: 3,
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.100',
                display: { xs: i === 0 ? 'block' : 'none', md: 'block' },
              }}
            >
              <Skeleton variant="rounded" width={110} height={24} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" width="60%" height={36} sx={{ mb: 1.5 }} />
              <Skeleton width="80%" />
              <Skeleton width="50%" sx={{ mb: 2 }} />
              <Skeleton variant="rounded" height={40} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SearchProgress;
